/**
 * Areas of Responsibility and the orbital view.
 *
 * The map opens at ORBIT — a globe showing every AOR as a point — and flies
 * down into one. Level-of-detail is derived from zoom and gates the HUD:
 * strategic (the globe) shows AOR points and the fly-in CTA; tactical shows the
 * full COP chrome.
 */

export interface Aor {
  id: string;
  sub: string;
  /** [lon, lat] */
  center: [number, number];
  zoom: number;
  pitch: number;
  bearing: number;
}

export const AORS: Aor[] = [
  { id: 'JAZIRA', sub: '36.29N 43.63E', center: [43.63, 36.29], zoom: 11.6, pitch: 55, bearing: 24 },
  { id: 'ANBAR WEST', sub: '33.42N 41.95E', center: [41.95, 33.42], zoom: 10.8, pitch: 52, bearing: 12 },
  { id: 'KHABUR VALLEY', sub: '36.55N 40.74E', center: [40.74, 36.55], zoom: 11.2, pitch: 54, bearing: 20 },
  { id: 'ZAGROS EDGE', sub: '35.55N 45.40E', center: [45.4, 35.55], zoom: 10.5, pitch: 50, bearing: 8 },
];

export const ORBIT = {
  center: [43.63, 26] as [number, number],
  zoom: 1.6,
  pitch: 0,
  bearing: 0,
};

export function aorById(id: string): Aor {
  return AORS.find((a) => a.id === id) ?? AORS[0]!;
}

/** How far from an AOR centre a track still counts as inside it. AORs are ~300 km
 *  apart, so this cleanly partitions the fleet without overlap. */
export const AOR_RADIUS_KM = 90;

/** Is a track at (lat, lon) inside this AOR? Equirectangular distance is plenty
 *  at this scale. A track with no fix belongs to no AOR. */
export function positionInAor(
  lat: number | undefined,
  lon: number | undefined,
  aor: Aor,
): boolean {
  if (lat === undefined || lon === undefined) return false;
  if (lat === 0 && lon === 0) return false;
  const dLat = lat - aor.center[1];
  const dLon = (lon - aor.center[0]) * Math.cos((aor.center[1] * Math.PI) / 180);
  const km = Math.sqrt(dLat * dLat + dLon * dLon) * 111;
  return km < AOR_RADIUS_KM;
}

/** The AOR a track is in, or null when it is outside all of them. */
export function aorForPosition(lat: number | undefined, lon: number | undefined): Aor | null {
  return AORS.find((a) => positionInAor(lat, lon, a)) ?? null;
}

export type MapLod = 'strategic' | 'regional' | 'tactical';

export function lodForZoom(zoom: number): MapLod {
  if (zoom < 5.5) return 'strategic';
  if (zoom < 9.5) return 'regional';
  return 'tactical';
}
